import { townColorHex as hex } from './townPropTemplates';
import type { TownPropPalette } from './townPropTemplates';

export type TownCivicTemplate = 'quayside_bench' | 'flower_planter' | 'notice_board' | 'ferry_waymarker';

/** Low 112×72 bench. Open space under the seat keeps the two planted feet readable. */
function bench(p: TownPropPalette): string {
  const wood = hex(p.primary), shade = hex(p.shade), edge = hex(p.woodEdge), iron = hex(p.iron);
  return `
    <g data-part="bench-frame" fill="${iron}">
      <path d="M17 15H23L26 67H30V72H15V67H19Z M89 15H95L93 67H97V72H82V67H87Z" />
      <path d="M21 46L41 64H71L91 46V51L73 67H39L20 51Z" />
    </g>
    <g data-part="bench-slats">
      <path d="M12 12L98 13L100 22L12 21Z M12 25L100 26L99 35L11 34Z" fill="${wood}" />
      <path d="M12 19L100 20V22L12 21Z M11 32L99 33V35L11 34Z" fill="${shade}" />
      <path d="M14 14L95 15 M14 27L95 28 M28 17L46 18 M61 30L84 31" stroke="${edge}" stroke-width="0.8" opacity="0.8" />
      <path d="M8 42L104 42L108 49H4Z" fill="${wood}" />
      <path d="M4 49H108V55H4Z" fill="${shade}" />
      <path d="M7 45H105 M5 49H107 M25 51H53 M65 52H91" stroke="${edge}" stroke-width="0.8" />
      <path d="M11 43L9 48 M35 43L34 48 M77 43L78 48 M101 43L103 48" stroke="${shade}" stroke-width="1" />
    </g>
    <g data-part="bench-armrests" fill="none" stroke="${iron}" stroke-width="3" stroke-linecap="round">
      <path d="M11 46V37Q11 32 18 34L24 37 M101 46V37Q101 32 94 34L88 37" />
    </g>
    <path d="M18 56L20 65 M91 56L90 65 M44 65H68" stroke="${edge}" stroke-width="0.8" />
    <g fill="${iron}"><circle cx="20" cy="17" r="1.3" /><circle cx="91" cy="18" r="1.3" />
      <circle cx="20" cy="30" r="1.3" /><circle cx="91" cy="31" r="1.3" /></g>`;
}

/** An 84×76 trough; foliage uses the primary palette while timber remains timber. */
function planter(p: TownPropPalette): string {
  const leaf = hex(p.primary), shade = hex(p.shade), wood = hex(p.wood), edge = hex(p.woodEdge);
  const iron = hex(p.iron), petal = hex(p.glass), core = hex(p.glassCore);
  const flower = (x: number, y: number) => `
    <path d="M${x} ${y + 2}Q${x + 3} 35 ${x - 1} 48" fill="none" stroke="${shade}" stroke-width="1.6" />
    <path d="M${x} ${y - 5}C${x + 4} ${y - 9} ${x + 7} ${y - 3} ${x + 4} ${y}
      C${x + 10} ${y + 1} ${x + 6} ${y + 8} ${x + 1} ${y + 4}
      C${x - 1} ${y + 10} ${x - 8} ${y + 5} ${x - 4} ${y + 1}
      C${x - 10} ${y - 1} ${x - 5} ${y - 8} ${x} ${y - 5}Z" fill="${petal}" />
    <circle cx="${x}" cy="${y}" r="2.6" fill="${core}" />`;
  return `
    <g data-part="planter-foliage">
      <path d="M38 49Q13 41 8 26Q30 23 38 49Z M43 48Q26 29 31 12Q49 22 43 48Z
        M49 48Q51 28 71 20Q73 41 49 48Z M60 49Q67 34 78 36Q75 49 60 49Z" fill="${leaf}" />
      <path d="M30 48Q12 44 14 36Q29 35 30 48Z M47 47Q40 31 48 24Q59 36 47 47Z
        M52 49Q57 38 69 39Q65 48 52 49Z" fill="${shade}" />
      <path d="M14 29L33 44 M34 19L40 39 M66 26L54 42" stroke="${edge}" stroke-width="0.7" opacity="0.65" />
      ${flower(19, 25)} ${flower(43, 12)} ${flower(65, 30)}
    </g>
    <g data-part="planter-trough">
      <path d="M8 47L76 47L71 70H13Z" fill="${wood}" />
      <path d="M10 49H74L72 53H12Z M13 65H72L71 70H13Z" fill="${shade}" />
      <path d="M6 44H78V49H6Z" fill="${edge}" />
      <path d="M9 45H75 M15 55L70 55 M16 62H69 M30 51L29 65 M55 51L56 65" stroke="${edge}" stroke-width="0.8" />
      <path d="M15 49L19 69 M67 49L64 69" stroke="${iron}" stroke-width="3" />
      <circle cx="16" cy="52" r="1" fill="${edge}" /><circle cx="66" cy="52" r="1" fill="${edge}" />
      <path d="M13 70H23V76H13Z M61 70H71V76H61Z" fill="${iron}" />
    </g>`;
}

/** A 112×140 sheltered civic board. Pegs and paper folds replace illegible small text. */
function notice(p: TownPropPalette): string {
  const wood = hex(p.wood), edge = hex(p.woodEdge), iron = hex(p.iron), shade = hex(p.shade);
  const board = hex(p.primary), roof = hex(p.roof), roofEdge = hex(p.roofEdge), paper = hex(p.glassCore);
  return `
    <g data-part="notice-posts">
      <path d="M19 24H28V137H31V140H16V137H19Z M84 24H93V137H96V140H81V137H84Z" fill="${wood}" />
      <path d="M21 103V134 M87 103V134" stroke="${edge}" stroke-width="1" />
      <path d="M16 135H31V140H16Z M81 135H96V140H81Z" fill="${iron}" />
      <path d="M27 110L42 98 M85 110L70 98" stroke="${wood}" stroke-width="5" />
    </g>
    <g data-part="notice-board">
      <path d="M15 24H97V103H15Z" fill="${wood}" />
      <path d="M20 29H92V97H20Z" fill="${board}" />
      <path d="M22 33H90 M22 93H90 M36 32V94 M56 32V94 M76 32V94" stroke="${shade}" stroke-width="1.1" />
      <path d="M17 27V99 M94 27V99 M20 100H92 M24 36L31 37 M62 86L72 85" stroke="${edge}" stroke-width="0.8" />
      <path d="M28 38L58 36L61 71L31 75Z" fill="${shade}" />
      <path d="M27 37L57 35L60 68L53 73L30 74Z" fill="${paper}" />
      <path d="M53 73L52 66L60 68Z" fill="${edge}" />
      <path d="M64 53L86 56L83 87L62 84Z" fill="${p.glass === p.glassCore ? edge : hex(p.glass)}" />
      <path d="M63 80L83 83 M33 64L49 62" stroke="${edge}" stroke-width="0.7" opacity="0.7" />
      <circle cx="42" cy="40" r="1.8" fill="${iron}" /><circle cx="75" cy="58" r="1.8" fill="${iron}" />
      <circle cx="42" cy="39.5" r="0.65" fill="${edge}" /><circle cx="75" cy="57.5" r="0.65" fill="${edge}" />
    </g>
    <g data-part="notice-roof">
      <path d="M4 23L56 4L108 23L105 29L56 12L7 29Z" fill="${roof}" />
      <path d="M7 25L56 8L105 25" fill="none" stroke="${roofEdge}" stroke-width="2" stroke-linejoin="round" />
      <path d="M18 24L19 20 M32 20L33 15 M46 15L46 10 M66 11L66 16 M80 16L80 20 M94 20L95 24"
        stroke="${shade}" stroke-width="1" />
      <path d="M8 29L56 13L104 29" fill="none" stroke="${wood}" stroke-width="1.5" />
    </g>`;
}

/** An 88×128 waymarker; opposite arrows and ferry/home pictograms carry its meaning. */
function waymarker(p: TownPropPalette): string {
  const wood = hex(p.wood), edge = hex(p.woodEdge), iron = hex(p.iron), bright = hex(p.glassCore);
  return `
    <g data-part="waymarker-post">
      <path d="M39 10H49V122H54V128H34V122H39Z" fill="${wood}" />
      <path d="M41 83V119 M46 86V117" stroke="${edge}" stroke-width="0.9" />
      <path d="M35 118H53V124H35Z" fill="${iron}" />
      <path d="M28 125L35 121H53L60 125V128H28Z" fill="${hex(p.stone)}" />
      <path d="M35 125H52" stroke="${edge}" stroke-width="0.8" />
      <circle cx="44" cy="9" r="5" fill="${iron}" /><path d="M42 6L45 6" stroke="${edge}" stroke-width="1" />
    </g>
    <g data-part="ferry-direction">
      <path d="M10 23L69 23L84 37L69 51H10Z" fill="${hex(p.primary)}" />
      <path d="M13 26H68L80 37L68 48H13Z" fill="none" stroke="${edge}" stroke-width="1.2" />
      <path d="M16 29L28 29 M59 44H69" stroke="${hex(p.shade)}" stroke-width="0.9" />
      <path d="M32 38H58L53 44H38Z M39 32H49V37H39Z M46 27H49V32H46Z" fill="${bright}" />
      <path d="M31 47Q35 44 39 47Q43 50 47 47Q51 44 55 47" fill="none" stroke="${bright}" stroke-width="1" />
      <path d="M41 33H44V35H41Z M46 33H48V35H46Z" fill="${iron}" />
      <circle cx="18" cy="37" r="1.8" fill="${iron}" />
    </g>
    <g data-part="town-direction">
      <path d="M19 58L78 58V84H19L4 71Z" fill="${hex(p.shade)}" />
      <path d="M20 61H75V81H20L8 71Z" fill="none" stroke="${hex(p.roofEdge)}" stroke-width="1.2" />
      <path d="M34 70L45 62L56 70L54 72L45 66L36 72Z M37 72L45 67L53 72V79H37Z" fill="${bright}" />
      <path d="M43 73H47V79H43Z" fill="${iron}" />
      <path d="M24 78H31 M59 64H66" stroke="${edge}" stroke-width="0.8" />
      <circle cx="70" cy="71" r="1.8" fill="${iron}" />
    </g>`;
}

/** Static texture bodies, each framed at its bottom-center ground anchor. */
export function buildTownCivicBody(template: TownCivicTemplate, palette: TownPropPalette): string {
  switch (template) {
  case 'quayside_bench': return bench(palette);
  case 'flower_planter': return planter(palette);
  case 'notice_board': return notice(palette);
  case 'ferry_waymarker': return waymarker(palette);
  }
}
